import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/glucose";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

const MAX_TRIES = 10;
const POLL_INTERVAL_MS = 2000;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed
  const triesRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        triesRef.current += 1;
        if (triesRef.current >= MAX_TRIES) {
          setStatus("failed");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setStatus("failed");
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] grid place-items-center" data-testid="payment-success-root">
      <div className="max-w-md w-full p-10 rounded-lg border border-border bg-card text-center">
        {status === "polling" && (
          <>
            <Spinner size={40} className="mx-auto mb-4 animate-spin" />
            <div className="font-display text-2xl mb-2">Confirming your payment…</div>
            <div className="text-sm text-muted-foreground">This usually takes a couple of seconds.</div>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={48} weight="duotone" className="mx-auto mb-4 sig-in-range" />
            <div className="font-display text-2xl mb-2" data-testid="payment-success-msg">Welcome to Pro 🎉</div>
            <div className="text-sm text-muted-foreground mb-6">Your subscription is active. All Pro features are unlocked.</div>
            <Button asChild className="w-full"><Link to="/dashboard" data-testid="back-to-dashboard-btn">Back to dashboard</Link></Button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle size={48} weight="duotone" className="mx-auto mb-4 sig-urgent" />
            <div className="font-display text-2xl mb-2">Couldn&apos;t confirm payment</div>
            <div className="text-sm text-muted-foreground mb-6">If money was deducted, it will reflect within a few minutes. Contact support if not.</div>
            <Button asChild variant="outline" className="w-full"><Link to="/pricing">Try again</Link></Button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-[70vh] grid place-items-center" data-testid="payment-cancel-root">
      <div className="max-w-md w-full p-10 rounded-lg border border-border bg-card text-center">
        <XCircle size={48} weight="duotone" className="mx-auto mb-4 text-muted-foreground" />
        <div className="font-display text-2xl mb-2">Checkout canceled</div>
        <div className="text-sm text-muted-foreground mb-6">No charge was made. You can try again anytime.</div>
        <Button asChild className="w-full"><Link to="/pricing" data-testid="back-to-pricing-btn">Back to plans</Link></Button>
      </div>
    </div>
  );
}
